import axios, { AxiosError } from "axios";

import { ApiError } from "@/types/api";

type UnauthorizedHandler = () => void;

export const UPSTREAM_SERVICE_MESSAGE =
  "ApplyKitty is temporarily unable to reach this service. Your existing work is still safe. Please retry in a moment.";

const CONNECTION_FAILURE_MESSAGE =
  "The service could not be reached. Please check your connection and retry.";
const DEFAULT_FAILURE_MESSAGE = "The request could not be completed.";
const UNSAFE_DOMAIN_MESSAGE =
  /<[^>]*>|\b(?:password|passwd|token|secret|stack|trace|exception|sqlstate)\b|\/srv\/|[a-z]:[\\/]/i;
const MAX_DOMAIN_MESSAGE_LENGTH = 240;
const MAX_JSON_ERROR_BLOB_BYTES = 32 * 1024;
const MAX_RETRY_AFTER_MS = 60_000;
const USER_STORE_MANAGED_ENDPOINTS = new Set([
  "/auth/me",
  "/auth/login",
  "/auth/login/totp",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/logout",
]);

let accessToken: string | null = null;
let unauthorizedHandler: UnauthorizedHandler | null = null;
let authenticationBoundaryGeneration = 0;
const requestAuthenticationGenerations = new WeakMap<object, number>();

export const http = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
  timeout: 15_000,
  withCredentials: true,
});

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

export function advanceAuthenticationBoundary(): void {
  authenticationBoundaryGeneration += 1;
}

function normalizeRequestEndpoint(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const candidate = value.trim();
  if (!candidate) return null;
  if (candidate.includes("\\")) return null;

  const pathEnd = candidate.search(/[?#]/);
  const rawPath = pathEnd === -1 ? candidate : candidate.slice(0, pathEnd);
  if (!rawPath || rawPath.includes("%")) return null;
  if (rawPath.startsWith("//")) return null;

  let endpoint: string;
  if (rawPath.startsWith("/")) {
    if (!rawPath.startsWith("/auth/")) return null;
    endpoint = rawPath;
  } else {
    if (!rawPath.startsWith("auth/")) return null;
    endpoint = `/${rawPath}`;
  }
  if (endpoint.split("/").some((segment) => segment === "." || segment === "..")) return null;
  if (endpoint.endsWith("/")) endpoint = endpoint.slice(0, -1);
  return endpoint || "/";
}

function isUserStoreManagedEndpoint(value: unknown): boolean {
  const endpoint = normalizeRequestEndpoint(value);
  return endpoint !== null && USER_STORE_MANAGED_ENDPOINTS.has(endpoint);
}

export function handleUnauthorizedResponse(
  response: Pick<Response, "status">,
  endpoint?: string | null,
  requestGeneration: number = authenticationBoundaryGeneration,
): void {
  if (
    response.status === 401
    && requestGeneration === authenticationBoundaryGeneration
    && !isUserStoreManagedEndpoint(endpoint)
  ) unauthorizedHandler?.();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function containsControlCharacters(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint !== undefined && (codePoint < 32 || codePoint === 127)) return true;
  }
  return false;
}

function safeDomainMessage(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const candidate = value.trim();
  if (!candidate || candidate.length > MAX_DOMAIN_MESSAGE_LENGTH) return null;
  if (containsControlCharacters(candidate) || UNSAFE_DOMAIN_MESSAGE.test(candidate)) return null;
  return candidate;
}

function isJsonMediaType(value: string): boolean {
  const essence = value.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  return essence === "application/json" || essence.endsWith("+json");
}

async function responsePayload(error: AxiosError): Promise<Record<string, unknown>> {
  const data: unknown = error.response?.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    if (
      data.size === 0
      || data.size > MAX_JSON_ERROR_BLOB_BYTES
      || !isJsonMediaType(data.type)
    ) return {};
    try {
      const parsed: unknown = JSON.parse(await data.text());
      return isRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return isRecord(data) ? data : {};
}

async function apiErrorFrom(error: unknown): Promise<ApiError> {
  if (error instanceof ApiError) return error;
  if (error instanceof AxiosError || axios.isAxiosError(error)) {
    const payload = await responsePayload(error);
    const status = error.response?.status;
    const timedOut = error.code === "ECONNABORTED" || error.code === "ETIMEDOUT";
    const domainMessage = safeDomainMessage(payload.error);
    const retryAfterValue = error.response?.headers?.["retry-after"];
    const retryAfterSeconds = typeof retryAfterValue === "string" || typeof retryAfterValue === "number"
      ? Number(retryAfterValue)
      : Number.NaN;
    const retryAfterMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds >= 0
      ? Math.min(Math.ceil(retryAfterSeconds * 1_000), MAX_RETRY_AFTER_MS)
      : undefined;
    let message: string;

    if (status === 502 || status === 503 || status === 504) {
      message = UPSTREAM_SERVICE_MESSAGE;
    } else if (status !== undefined && status >= 500 && status < 600) {
      message = "The service could not complete the request. Please try again.";
    } else if (timedOut) {
      message = "This request is taking longer than expected. Please try again.";
    } else if (!error.response) {
      message = CONNECTION_FAILURE_MESSAGE;
    } else if (
      status !== undefined &&
      status >= 400 &&
      status < 500 &&
      domainMessage
    ) {
      message = domainMessage;
    } else {
      message = DEFAULT_FAILURE_MESSAGE;
    }

    return new ApiError(message, {
      code: typeof payload.code === "string" ? payload.code : (error.code ?? undefined),
      endpoint: error.config?.url ?? "",
      retryAfterMs,
      status,
    });
  }
  return new ApiError(DEFAULT_FAILURE_MESSAGE);
}

http.interceptors.request.use((config) => {
  requestAuthenticationGenerations.set(config, authenticationBoundaryGeneration);
  if (accessToken) {
    config.headers.set("Authorization", `Bearer ${accessToken}`);
  } else {
    config.headers.delete("Authorization");
  }
  return config;
});

http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    const normalized = await apiErrorFrom(error);
    const endpoint = error instanceof AxiosError || axios.isAxiosError(error)
      ? normalizeRequestEndpoint(error.config?.url)
      : null;
    const requestGeneration = error instanceof AxiosError || axios.isAxiosError(error)
      ? requestAuthenticationGenerations.get(error.config ?? {})
      : undefined;
    handleUnauthorizedResponse(
      { status: normalized.status ?? 0 },
      endpoint,
      requestGeneration,
    );
    throw normalized;
  },
);
